/*
 * This file contains a pagination component that allows users to navigate through a list of items.
 */

import React from "react";
import styles from "./pagination.module.scss";

const ArrowRightIcon = ({ className }) => (
  <svg width="47" height="14" viewBox="0 0 47 14" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M0 7L44 7" stroke="#403D3A" strokeWidth="2"/>
    <path d="M37.9961 1L44.273 7L37.9961 13" stroke="#403D3A" strokeWidth="2"/>
  </svg>
);

const Pagination = ({ currentPage = 1, totalPages = 1, onPageChange }) => {
  const validCurrentPage = isNaN(currentPage) ? 1 : currentPage;
  const validTotalPages = isNaN(totalPages) ? 1 : totalPages;

  const scrollToResultsCount = () => {
    if (typeof window === "undefined") return;
    const target = document.querySelector('[class*="ResultsCount"]');
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (validTotalPages <= 1) return;

  const handlePageClick = (page) => {
    if (page !== validCurrentPage) {
      onPageChange(page);
      scrollToResultsCount();
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];

    if (validTotalPages <= 4) {
      for (let i = 1; i <= validTotalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      if (validCurrentPage > 3) {
        pageNumbers.push("...");
      }

      const start = Math.max(2, validCurrentPage - 1);
      const end = Math.min(validTotalPages - 1, validCurrentPage + 1);

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (validCurrentPage < validTotalPages - 2) {
        pageNumbers.push("...");
      }

      if (!pageNumbers.includes(validTotalPages)) {
        pageNumbers.push(validTotalPages);
      }
    }

    return pageNumbers.map((page, index) =>
      page === "..." ? (
        <span key={`ellipsis-${index}`} className={styles.dots}>
          ...
        </span>
      ) : (
        <span
          key={page}
          className={`${styles.pageNumber} ${
            page === validCurrentPage ? styles.active : ""
          }`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </span>
      )
    );
  };

  return (
    <div className={styles.pagination}>
      <span
        className={`${styles.arrow} ${
          validCurrentPage === 1 ? styles.disabled : ""
        }`}
        onClick={() => {
          if (validCurrentPage > 1) {
            onPageChange(validCurrentPage - 1);
            scrollToResultsCount();
          }
        }}
      >
        <ArrowRightIcon className={styles.arrowLeft} />
      </span>

      {renderPageNumbers()}

      <span
        className={`${styles.arrow} ${
          validCurrentPage === validTotalPages ? styles.disabled : ""
        }`}
        onClick={() => {
          if (validCurrentPage < validTotalPages) {
            onPageChange(validCurrentPage + 1);
            scrollToResultsCount();
          }
        }}
      >
        <ArrowRightIcon className={styles.arrowRight} />
      </span>
    </div>
  );
};

export default Pagination;
